import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Send, Search, ArrowLeft, Loader2, User, Check, CheckCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

export default function Messages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser.user_id);
      const interval = setInterval(() => fetchMessages(selectedUser.user_id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedUser]);

  useEffect(() => {
    scrollToBottom();
    // Focus input after messages update
    if (selectedUser && inputRef.current) {
      inputRef.current.focus();
    }
  }, [messages, selectedUser]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async () => {
    try {
      const response = await api.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await api.getMessages(userId);
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setSending(true);
    try {
      await api.sendMessage({ receiver_id: selectedUser.user_id, content: newMessage.trim() });
      setNewMessage("");
      fetchMessages(selectedUser.user_id);
      fetchConversations();
    } catch (error) {
      toast.error("Mesaj gönderilemedi");
    } finally {
      setSending(false);
    }
    
    // Focus input after all state updates and re-renders complete
    requestAnimationFrame(() => {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    });
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await api.searchUsers(query);
      setSearchResults(response.data.filter(u => u.user_id !== user?.user_id));
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  const startConversation = (targetUser) => {
    setSelectedUser(targetUser);
    setSearchQuery("");
    setSearchResults([]);
    setMessages([]);
    fetchMessages(targetUser.user_id);
    // Focus input when conversation starts
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Şimdi";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}dk`;
    if (diff < 86400000) return date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    return date.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="messages-page">
      <div className="glass-card overflow-hidden" style={{ height: "calc(100vh - 200px)", minHeight: "500px" }}>
        <div className="flex h-full">
          {/* Conversations List */}
          <div className={`w-full md:w-80 border-r border-white/10 flex flex-col ${selectedUser ? "hidden md:flex" : "flex"}`}>
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <h1 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-3">
                Mesajlar
              </h1>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Kullanıcı ara..."
                  className="pl-9 bg-[#050E2E]/50 border-white/10 text-white h-10"
                  data-testid="user-search-input"
                />
              </div>
            </div>

            {/* Search Results */}
            {searchQuery.length >= 2 && (
              <div className="border-b border-white/10 max-h-48 overflow-y-auto">
                {searching ? (
                  <div className="p-4 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-[#4DFFFF] mx-auto" />
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((u) => (
                    <button
                      key={u.user_id}
                      onClick={() => startConversation(u)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <span className="text-sm font-bold text-[#C6A87C]">{u.name?.charAt(0)}</span>
                        )}
                      </div>
                      <div className="text-left">
                        <p className="text-sm text-white font-medium">{u.name}</p>
                        <p className="text-xs text-[#64748B]">@{u.username}</p>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-4 text-center text-sm text-[#64748B]">
                    Kullanıcı bulunamadı
                  </div>
                )}
              </div>
            )}

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {conversations.length > 0 ? (
                conversations.map((conv) => (
                  <button
                    key={conv.user_id}
                    onClick={() => startConversation(conv)}
                    className={`w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors border-b border-white/5 ${
                      selectedUser?.user_id === conv.user_id ? "bg-white/10" : ""
                    }`}
                    data-testid={`conversation-${conv.user_id}`}
                  >
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                        {conv.picture ? (
                          <img src={conv.picture} alt={conv.name} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <span className="text-lg font-bold text-[#C6A87C]">{conv.name?.charAt(0)}</span>
                        )}
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#F61225] text-white text-xs flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-white font-medium truncate">{conv.name}</p>
                        <span className="text-xs text-[#64748B] flex-shrink-0">{formatTime(conv.last_message_at)}</span>
                      </div>
                      <p className="text-xs text-[#64748B] truncate mt-0.5">
                        {conv.last_message}
                      </p>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-8 text-center">
                  <MessageCircle className="w-12 h-12 text-[#64748B] mx-auto mb-3" />
                  <p className="text-[#B1C1C1]">Henüz mesajın yok</p>
                  <p className="text-sm text-[#64748B] mt-1">Yukarıdan kullanıcı ara ve mesajlaşmaya başla</p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${!selectedUser ? "hidden md:flex" : "flex"}`}>
            {selectedUser ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-white/10 flex items-center gap-3">
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="md:hidden p-2 hover:bg-white/10 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5 text-white" />
                  </button>
                  <Link to={`/profile/${selectedUser.username}`} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                      {selectedUser.picture ? (
                        <img src={selectedUser.picture} alt={selectedUser.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold text-[#C6A87C]">{selectedUser.name?.charAt(0)}</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-white font-medium">{selectedUser.name}</p>
                      <p className="text-xs text-[#64748B]">@{selectedUser.username}</p>
                    </div>
                  </Link>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length > 0 ? (
                    messages.map((msg) => {
                      const isOwn = msg.sender_id === user?.user_id;
                      return (
                        <div
                          key={msg.message_id}
                          className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                              isOwn
                                ? "bg-[#4DFFFF] text-[#050E2E] rounded-br-md"
                                : "bg-white/10 text-white rounded-bl-md"
                            }`}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? "text-[#050E2E]/60" : "text-[#64748B]"}`}>
                              <span className="text-xs">{formatTime(msg.created_at)}</span>
                              {isOwn && (
                                msg.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <MessageCircle className="w-12 h-12 text-[#64748B] mx-auto mb-3" />
                        <p className="text-[#B1C1C1]">Henüz mesaj yok</p>
                        <p className="text-sm text-[#64748B]">Sohbeti başlat!</p>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <Input
                      ref={inputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Mesaj yaz..."
                      className="flex-1 bg-[#050E2E]/50 border-white/10 text-white h-11"
                      disabled={sending}
                      autoFocus
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 h-11 px-4"
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
                  <h2 className="text-xl font-bold text-white mb-2">Mesajlarınız</h2>
                  <p className="text-[#B1C1C1]">Bir sohbet seçin veya yeni bir konuşma başlatın</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
